/**
 * RagDropZone — drag/drop file uploader bound to a session and an optional
 * persona. Lists already-ingested documents with progress + delete.
 *
 * Used in two places:
 *   1. Wizard "Belgeler" step → personaId omitted (shared council pool)
 *   2. Persona drawer → personaId={persona.id} (private to that persona)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  File as FileIcon,
  Trash2,
  Upload,
  AlertCircle,
  CheckCircle2,
  Loader2,
  RotateCw,
} from "lucide-react";
import {
  deleteDocument,
  listDocuments,
  uploadDocument,
  type RagDocument,
} from "../../transport/ragClient";
import { useDebateStore } from "../../store/debateStore";
import { cn } from "../common/cn";

interface UploadingItem {
  key: string;
  filename: string;
  loaded: number;
  total: number;
  error?: string;
  /** Original File reference kept so the user can retry without re-picking. */
  file: File;
}

interface Props {
  /** When omitted ⇒ pool. */
  personaId?: string | undefined;
  scopeLabel: string;
  helpText: string;
  className?: string;
}

const ACCEPT = ".pdf,.docx,.xlsx,.txt,.md,.csv,.html";
const MAX_BYTES = 20 * 1024 * 1024;

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function RagDropZone({
  personaId,
  scopeLabel,
  helpText,
  className,
}: Props) {
  const ensureSession = useDebateStore((s) => s.ensurePendingSessionId);
  const setStagedDocCount = useDebateStore((s) => s.setStagedDocCount);
  const [docs, setDocs] = useState<RagDocument[]>([]);
  const [uploading, setUploading] = useState<UploadingItem[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Session id is minted lazily on first interaction so an empty wizard
  // does NOT spam pending session rows.
  const refreshList = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const sid = ensureSession();
        const scope = personaId ?? "pool";
        const data = await listDocuments({
          sessionId: sid,
          personaId: scope,
          ...(signal ? { signal } : {}),
        });
        setDocs(data);
        setListError(null);
        // Only the shared pool speaks for the debate as a whole; a
        // persona-private drop must not inflate the launch-time count.
        if (!personaId) {
          setStagedDocCount(data.length);
        }
      } catch (e) {
        if ((e as DOMException)?.name === "AbortError") return;
        setListError((e as Error)?.message ?? String(e));
      }
    },
    [ensureSession, personaId, setStagedDocCount],
  );

  useEffect(() => {
    const ac = new AbortController();
    void refreshList(ac.signal);
    return () => ac.abort();
  }, [refreshList]);

  const _runUpload = useCallback(
    async (key: string, sid: string, f: File) => {
      setUploading((u) =>
        u.map((x) => {
          if (x.key !== key) return x;
          const { error: _drop, ...rest } = x;
          void _drop;
          return { ...rest, loaded: 0, total: f.size };
        }),
      );
      try {
        await uploadDocument({
          sessionId: sid,
          ...(personaId ? { personaId } : {}),
          file: f,
          retries: 1,
          onProgress: (loaded, total) =>
            setUploading((u) =>
              u.map((x) => (x.key === key ? { ...x, loaded, total } : x)),
            ),
        });
        setUploading((u) => u.filter((x) => x.key !== key));
        await refreshList();
      } catch (e) {
        const msg = (e as Error)?.message ?? String(e);
        setUploading((u) =>
          u.map((x) => (x.key === key ? { ...x, error: msg } : x)),
        );
      }
    },
    [personaId, refreshList],
  );

  const onFiles = useCallback(
    async (files: FileList | File[] | null) => {
      if (!files || files.length === 0) return;
      const sid = ensureSession();
      const arr = Array.from(files);
      for (const f of arr) {
        if (f.size > MAX_BYTES) {
          setListError(`${f.name}: en fazla ${fmtBytes(MAX_BYTES)} olabilir.`);
          continue;
        }
        const key = `${f.name}-${f.size}-${Date.now()}-${Math.random()}`;
        setUploading((u) => [
          ...u,
          { key, filename: f.name, loaded: 0, total: f.size, file: f },
        ]);
        await _runUpload(key, sid, f);
      }
    },
    [ensureSession, _runUpload],
  );

  const onRetry = useCallback(
    (key: string) => {
      const sid = ensureSession();
      const item = uploading.find((x) => x.key === key);
      if (!item) return;
      void _runUpload(key, sid, item.file);
    },
    [ensureSession, uploading, _runUpload],
  );

  const onCancel = useCallback((key: string) => {
    setUploading((u) => u.filter((x) => x.key !== key));
  }, []);

  const onDelete = useCallback(
    async (id: string) => {
      try {
        await deleteDocument(id);
        await refreshList();
      } catch (e) {
        setListError((e as Error)?.message ?? String(e));
      }
    },
    [refreshList],
  );

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    void onFiles(e.dataTransfer?.files ?? null);
  };

  const totalReady = useMemo(
    () => docs.filter((d) => d.status === "ready").length,
    [docs],
  );

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <div
        onDragEnter={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
        }}
        className={cn(
          "glass focus-ring relative cursor-pointer rounded-xl border-2 border-dashed p-5 transition",
          dragActive
            ? "border-cyan-300/60 bg-cyan-500/[0.04]"
            : "border-white/10 hover:border-white/20",
        )}
      >
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 flex-none place-items-center rounded-full border border-cyan-300/30 bg-cyan-500/10 text-cyan-100">
            <Upload className="h-4 w-4" strokeWidth={1.6} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-fg-strong">
              {scopeLabel}
            </div>
            <div className="mt-0.5 text-[11px] leading-relaxed text-fg-subtle">
              {helpText} · PDF, DOCX, XLSX, TXT, MD, CSV, HTML · max{" "}
              {fmtBytes(MAX_BYTES)}.
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-wider text-fg-subtle">
            {totalReady} hazır
          </div>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple
          className="hidden"
          onChange={(e) => void onFiles(e.target.files)}
        />
      </div>

      {listError ? (
        <div className="flex items-center gap-2 rounded-lg border border-rose-500/30 bg-rose-500/[0.06] px-3 py-2 text-[11px] text-rose-200">
          <AlertCircle className="h-3.5 w-3.5" />
          <span>{listError}</span>
        </div>
      ) : null}

      <ul className="flex flex-col gap-1.5">
        <AnimatePresence>
          {uploading.map((u) => (
            <motion.li
              key={u.key}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              className={cn(
                "rounded-lg border px-3 py-2 text-xs",
                u.error
                  ? "border-rose-500/30 bg-rose-500/[0.06]"
                  : "border-white/10 bg-surface-0/40",
              )}
            >
              <div className="flex items-center gap-2">
                {u.error ? (
                  <AlertCircle className="h-3.5 w-3.5 flex-none text-rose-300" />
                ) : (
                  <Loader2 className="h-3.5 w-3.5 flex-none animate-spin text-cyan-200" />
                )}
                <span
                  className="min-w-0 flex-1 truncate text-fg-strong"
                  title={u.filename}
                >
                  {u.filename}
                </span>
                {u.error ? (
                  <>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRetry(u.key);
                      }}
                      title="Yeniden dene"
                      className="focus-ring flex flex-none items-center gap-1 rounded-md border border-white/10 bg-white/[0.04] px-2 py-1 text-[10px] uppercase tracking-wider text-fg-strong hover:bg-white/[0.08]"
                    >
                      <RotateCw className="h-3 w-3" strokeWidth={1.6} />
                      Yeniden dene
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCancel(u.key);
                      }}
                      title="Sil"
                      className="focus-ring flex-none rounded-md p-1 text-fg-subtle hover:bg-white/[0.05] hover:text-rose-300"
                    >
                      <Trash2 className="h-3 w-3" strokeWidth={1.6} />
                    </button>
                  </>
                ) : (
                  <span className="text-fg-subtle">
                    {Math.min(
                      100,
                      Math.round((u.loaded / Math.max(u.total, 1)) * 100),
                    )}
                    %
                  </span>
                )}
              </div>
              {/* Hata gerekçesi kendi satırında ve TAM olarak durur. Aynı
                  satırda 12rem'lik bir kutuya sıkıştırıldığında kullanıcı
                  "extract …" dışında hiçbir şey göremiyor, dolayısıyla
                  dosyayı neyin reddettiğini de bilemiyordu. */}
              {u.error ? (
                <p className="mt-1.5 pl-[1.375rem] leading-relaxed text-rose-200">
                  {u.error}
                </p>
              ) : null}
            </motion.li>
          ))}
          {docs.map((d) => (
            <motion.li
              key={d.id}
              layout
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              className="group flex items-center gap-2 rounded-lg border border-white/[0.06] bg-surface-0/40 px-3 py-2 text-xs"
            >
              {d.status === "ready" ? (
                <CheckCircle2 className="h-3.5 w-3.5 flex-none text-emerald-300" />
              ) : d.status === "failed" ? (
                <AlertCircle className="h-3.5 w-3.5 flex-none text-rose-300" />
              ) : (
                <Loader2 className="h-3.5 w-3.5 flex-none animate-spin text-fg-subtle" />
              )}
              <FileIcon className="h-3.5 w-3.5 flex-none text-fg-subtle" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-fg-strong" title={d.filename}>
                  {d.filename}
                </div>
                <div className="text-[10px] text-fg-subtle">
                  {fmtBytes(d.byte_size)}
                  {d.page_count ? ` · ${d.page_count} sayfa` : ""}
                  {d.chunk_count ? ` · ${d.chunk_count} parça` : ""}
                  {d.error ? ` · ${d.error}` : ""}
                </div>
              </div>
              <button
                type="button"
                onClick={() => void onDelete(d.id)}
                title="Sil"
                className="focus-ring rounded-md p-1 text-fg-subtle opacity-0 transition group-hover:opacity-100 hover:bg-white/[0.05] hover:text-rose-300"
              >
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.6} />
              </button>
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>
    </div>
  );
}
